import { Product, ProductOption, ProductOptionValue } from '../types';

const N8N_WEBHOOK_BASE = 'https://n8n-34gc.onrender.com/webhook';
const PRODUCTS_WEBHOOK_ID = 'bea19d18-3c3a-4410-b85a-18c0bf855f7b';
const DEFAULT_IMAGE = 'https://img.adminteam.work/smalllec.png';

const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const convertToSpreadsheetFormat = (product: Omit<Product, 'id'>): Record<string, any> => {
  const spreadsheetProduct: Record<string, any> = {
    id: generateUUID(),
    'Product Name': product.name,
    'Price': product.price,
    'Image': product.image || DEFAULT_IMAGE,
    'Description': product.description,
  };

  // Initialize all option fields with empty values
  for (let i = 1; i <= 10; i++) {
    spreadsheetProduct[`Option${i}`] = '';
    spreadsheetProduct[`Option${i}_Value`] = '';
    spreadsheetProduct[`Option${i}_Ad_Cost`] = '';
  }

  // Fill in the actual options
  product.options.forEach((option, index) => {
    if (index < 10) {
      const optionNumber = index + 1;
      const values = option.values.map(v => v.value);
      const costs = option.values.map(v => v.additionalCost);

      spreadsheetProduct[`Option${optionNumber}`] = option.name;
      spreadsheetProduct[`Option${optionNumber}_Value`] = values.join(',');
      spreadsheetProduct[`Option${optionNumber}_Ad_Cost`] = costs.join(',');
    }
  });

  return spreadsheetProduct;
};

const convertFromSpreadsheetFormat = (spreadsheetProduct: any): Product | null => {
  try {
    const options: ProductOption[] = [];

    // Convert option columns back to options array
    for (let i = 1; i <= 10; i++) {
      const nameKey = `Option${i}`;
      const valuesKey = `Option${i}_Value`;
      const costsKey = `Option${i}_Ad_Cost`;
      
      if (spreadsheetProduct[nameKey] && spreadsheetProduct[valuesKey]) {
        const values = String(spreadsheetProduct[valuesKey]).split(',').map(v => v.trim());
        const costs = String(spreadsheetProduct[costsKey] || '').split(',').map(c => parseFloat(c) || 0);
        
        const optionValues: ProductOptionValue[] = values.map((value, index) => ({
          value,
          additionalCost: costs[index] || 0
        }));

        if (optionValues.length > 0) {
          options.push({
            name: spreadsheetProduct[nameKey],
            values: optionValues
          });
        }
      }
    }

    const id = spreadsheetProduct.id || generateUUID();
    const image = spreadsheetProduct['Image'] || DEFAULT_IMAGE;

    return {
      id,
      name: String(spreadsheetProduct['Product Name'] || ''),
      price: Number(spreadsheetProduct['Price'] || 0),
      image: String(image === '' ? DEFAULT_IMAGE : image),
      description: String(spreadsheetProduct['Description'] || ''),
      options: options
    };
  } catch (error) {
    console.error('Error converting product:', error, spreadsheetProduct);
    return null;
  }
};

const fetchWithRetry = async (url: string, options: RequestInit, retries = 3): Promise<Response> => {
  for (let i = 0; i < retries; i++) {
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      if (i === retries - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
    }
  }
  throw new Error('Failed after retries');
};

export const productService = {
  getProducts: async (): Promise<Product[]> => {
    try {
      const response = await fetchWithRetry(
        `${N8N_WEBHOOK_BASE}/${PRODUCTS_WEBHOOK_ID}`,
        {
          method: 'POST',
          body: JSON.stringify({ type: 'get_products' })
        }
      );

      const data = await response.json();
      
      if (!data) {
        throw new Error('No data received from server');
      }

      let products = Array.isArray(data) ? data : [data];

      const convertedProducts = products
        .map(convertFromSpreadsheetFormat)
        .filter((product): product is Product => product !== null);

      if (convertedProducts.length === 0) {
        console.warn('No valid products found in response');
      }

      return convertedProducts;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch products');
    }
  },
  
  addProduct: async (product: Omit<Product, 'id'>): Promise<Product> => {
    try {
      const spreadsheetProduct = convertToSpreadsheetFormat(product);
      
      const response = await fetchWithRetry(
        `${N8N_WEBHOOK_BASE}/${PRODUCTS_WEBHOOK_ID}`,
        {
          method: 'POST',
          body: JSON.stringify({
            type: 'product_create',
            data: spreadsheetProduct
          })
        }
      );
      
      const result = await response.json();
      const converted = convertFromSpreadsheetFormat(result.product || result);
      
      if (!converted) {
        throw new Error('Failed to convert product data');
      }
      
      return converted;
    } catch (error) {
      console.error('Error adding product:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to add product');
    }
  },

  updateProduct: async (product: Product): Promise<Product> => {
    try {
      const spreadsheetProduct = {
        ...convertToSpreadsheetFormat(product),
        id: product.id
      };
      
      const response = await fetchWithRetry(
        `${N8N_WEBHOOK_BASE}/${PRODUCTS_WEBHOOK_ID}`,
        {
          method: 'POST',
          body: JSON.stringify({
            type: 'product_update',
            data: spreadsheetProduct
          })
        }
      );
      
      const result = await response.json();
      const converted = convertFromSpreadsheetFormat(result.product || result);
      
      if (!converted) {
        throw new Error('Failed to convert product data');
      }
      
      return converted;
    } catch (error) {
      console.error('Error updating product:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to update product');
    }
  },

  deleteProduct: async (id: string): Promise<void> => {
    try {
      await fetchWithRetry(
        `${N8N_WEBHOOK_BASE}/${PRODUCTS_WEBHOOK_ID}`,
        {
          method: 'POST',
          body: JSON.stringify({
            type: 'product_delete',
            data: { id }
          })
        }
      );
    } catch (error) {
      console.error('Error deleting product:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to delete product');
    }
  }
};
